import React from 'react';
import { ArrowRight, BookOpen, Clock, Zap } from 'lucide-react';
import { AcademyProgress } from '../../config/academyConfig';

interface ProgressTrackerProps {
  progress?: AcademyProgress;
  isAuthenticated: boolean;
  onLoginClick: () => void;
}

const ProgressTracker: React.FC<ProgressTrackerProps> = ({
  progress,
  isAuthenticated,
  onLoginClick
}) => {
  const totalModules = 6;
  const completedCount = progress?.modulesCompleted?.length || 0;
  const progressPercent = Math.round((completedCount / totalModules) * 100);

  // Guest view - minimal, compelling
  if (!isAuthenticated) {
    return (
      <div className="bg-neutral-900 rounded-3xl p-6 text-white">
        <p className="text-sm font-medium text-neutral-400 mb-2">Free Masterclass</p>
        <h2 className="text-2xl font-bold tracking-tight mb-2">
          Learn the coffee business
        </h2>
        <p className="text-neutral-400 text-sm mb-5">
          6 modules. Everything you need to launch.
        </p>
        <button
          onClick={onLoginClick}
          className="inline-flex items-center bg-white text-neutral-900 px-5 py-3 rounded-xl font-semibold text-sm transition-all hover:bg-neutral-100 active:scale-[0.98]"
        >
          Start Learning
          <ArrowRight className="w-4 h-4 ml-2" />
        </button>
      </div>
    );
  }

  // Authenticated view - progress focused
  return (
    <div className="space-y-3">
      {/* Progress Card */}
      <div className="bg-white rounded-3xl p-5 border border-neutral-200/60">
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm font-medium text-neutral-400">Your Progress</p>
          <p className="text-sm font-semibold text-neutral-900">{completedCount}/{totalModules}</p>
        </div>

        {/* Progress Bar */}
        <div className="h-2 bg-neutral-100 rounded-full overflow-hidden mb-4">
          <div
            className="h-full bg-neutral-900 rounded-full transition-all duration-500"
            style={{ width: `${progressPercent}%` }}
          />
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-3 gap-3">
          <div className="text-center">
            <div className="w-10 h-10 bg-neutral-50 rounded-xl flex items-center justify-center mx-auto mb-2">
              <BookOpen className="w-5 h-5 text-neutral-600" />
            </div>
            <p className="text-lg font-bold text-neutral-900">{completedCount}</p>
            <p className="text-xs text-neutral-400">Done</p>
          </div>
          <div className="text-center">
            <div className="w-10 h-10 bg-neutral-50 rounded-xl flex items-center justify-center mx-auto mb-2">
              <Clock className="w-5 h-5 text-neutral-600" />
            </div>
            <p className="text-lg font-bold text-neutral-900">{completedCount * 45}m</p>
            <p className="text-xs text-neutral-400">Learned</p>
          </div>
          <div className="text-center">
            <div className="w-10 h-10 bg-neutral-50 rounded-xl flex items-center justify-center mx-auto mb-2">
              <Zap className="w-5 h-5 text-neutral-600" />
            </div>
            <p className="text-lg font-bold text-neutral-900">{totalModules - completedCount}</p>
            <p className="text-xs text-neutral-400">Left</p>
          </div>
        </div>
      </div>

      {/* Continue CTA */}
      {progress?.currentModule && (
        <button className="w-full bg-neutral-900 text-white p-4 rounded-2xl flex items-center justify-between transition-all hover:bg-neutral-800 active:scale-[0.99]">
          <div className="text-left">
            <p className="text-xs text-neutral-400">Continue</p>
            <p className="font-semibold">{progress.currentModule}</p>
          </div>
          <ArrowRight className="w-5 h-5" />
        </button>
      )}
    </div>
  );
};

export default ProgressTracker;
